import React from 'react'

import { LocalizedLink } from '@rewardops/ui'

// Use to replace placeholders in translation strings
export const replacePlaceholders = (
  str: string,
  value: Record<string, any>,
) => {
  return str.replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
    return value.hasOwnProperty(key) ? value[key] : match
  })
}

// Use to replace placeholders in translation strings and format points
export const replacePlaceholdersForPoints = (
  str: string,
  value: Record<string, any>,
  locale: string,
  maximumFractionDigits = 2,
) => {
  return str.replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
    if (value.hasOwnProperty(key)) {
      const num = value[key]
      if (typeof num === 'number') {
        const isInteger = num % 1 === 0
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: isInteger ? 0 : maximumFractionDigits,
          maximumFractionDigits,
        }).format(num)
      }
      return value[key]
    }
    return match
  })
}

// Use to replace placeholders in translation strings with link
export const replacePlaceholdersWithLink = (
  str: string,
  link: string,
  className?: string,
): React.ReactNode[] => {
  const regex = /\{\{([^}]+)\}\}/g
  const parts = []
  let lastIndex = 0
  let match

  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push(str.slice(lastIndex, match.index))
    }
    parts.push(match[0])
    lastIndex = regex.lastIndex
  }

  if (lastIndex < str.length) {
    parts.push(str.slice(lastIndex))
  }

  // This is for the case it includes spaces
  return parts.map((part, index) => {
    const placeholderMatch = part.match(/^\{\{([^}]+)\}\}$/)
    if (placeholderMatch) {
      const text = placeholderMatch[1]
      return (
        <LocalizedLink key={index} href={link} className={className}>
          {text}
        </LocalizedLink>
      )
    }
    return part
  })
}

// Use to replace placeholders in translation strings with links
export const replacePlaceholdersWithLinks = (
  str: string,
  values: Record<string, React.ReactNode>,
): React.ReactNode[] => {
  const parts: React.ReactNode[] = []
  const regex = /\{\{(\w+)\}\}/g
  let lastIndex = 0
  let match
  let index = 0

  while ((match = regex.exec(str)) !== null) {
    const [placeholder, key] = match
    const startIndex = match.index

    if (startIndex > lastIndex) {
      parts.push(
        <React.Fragment key={`text-${index}`}>
          {str.slice(lastIndex, startIndex)}
        </React.Fragment>,
      )
      index++
    }

    if (key && key in values) {
      parts.push(
        <React.Fragment key={`node-${index}`}>{values[key]}</React.Fragment>,
      )
      index++
    } else {
      parts.push(
        <React.Fragment key={`missing-${index}`}>{placeholder}</React.Fragment>,
      )
      index++
    }

    lastIndex = regex.lastIndex
  }

  if (lastIndex < str.length) {
    parts.push(
      <React.Fragment key={`text-${index}`}>
        {str.slice(lastIndex)}
      </React.Fragment>,
    )
  }

  return parts
}
